import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { fbqGrantConsent, fbqRevokeConsent } from '../utils/metaPixel';

export interface CookieConsent {
  necessary: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  consent: CookieConsent | null; // null = not yet decided
  hasDecided: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: Omit<CookieConsent, 'necessary'>) => void;
  openSettings: () => void;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (open: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

const CONSENT_KEY = 'cookieConsent';

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return null;
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const hasDecided = consent !== null;

  const saveConsent = useCallback((newConsent: CookieConsent) => {
    setConsent(newConsent);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
    if (newConsent.marketing) {
      fbqGrantConsent();
    } else {
      fbqRevokeConsent();
    }
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  }, [saveConsent]);

  const savePreferences = useCallback((preferences: Omit<CookieConsent, 'necessary'>) => {
    saveConsent({ necessary: true, ...preferences });
    setIsSettingsOpen(false);
  }, [saveConsent]);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const value = useMemo(() => ({
    consent,
    hasDecided,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    isSettingsOpen,
    setIsSettingsOpen,
  }), [consent, hasDecided, acceptAll, rejectAll, savePreferences, openSettings, isSettingsOpen]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return context;
};
